import { useEffect } from 'react';
import {
  Box,
  Container,
  Divider,
  Link,
  Stack,
  Typography,
} from '@mui/material';

/**
 * Public account-deletion procedure page — exposed at /account-deletion.
 *
 * <p>Required by the Google Play Data Safety form: the URL declared there
 * must be reachable WITHOUT login and must spell out (1) the deletion
 * procedure, (2) what data is wiped, (3) what is retained and for how
 * long. The signed-in flow (Account → Delete my account) does the actual
 * deletion; this page only documents how to find and use it.</p>
 *
 * <p>Strings are hardcoded in French because the Play Store listing is
 * fr-FR and Google reviewers read this URL once during review. If the
 * app later targets more locales, lift these into the i18n bundle next
 * to {@code legal.privacy.*}.</p>
 */
export function AccountDeletionPage() {
  useEffect(() => {
    document.title = 'Suppression de compte — AfroEspace';
  }, []);

  return (
    <Container maxWidth="md" sx={{ py: { xs: 4, md: 6 } }}>
      <Typography
        variant="overline"
        color="text.secondary"
        sx={{ letterSpacing: '0.12em', fontWeight: 700 }}
      >
        AfroEspace · Vos données
      </Typography>
      <Typography variant="h3" sx={{ mt: 1, mb: 4 }}>
        Suppression de votre compte
      </Typography>

      <Box sx={{ '& p, & li': { lineHeight: 1.8, color: 'text.primary' } }}>
        <Stack spacing={1}>
          <Typography>
            Vous pouvez supprimer votre compte AfroEspace à tout moment et
            sans frais. Cette page décrit la procédure ainsi que les données
            concernées par la suppression et celles que nous conservons pour
            des raisons légales.
          </Typography>
        </Stack>

        <Section title="1. Comment supprimer votre compte">
          <Typography>
            Vous pouvez supprimer votre compte vous-même, en quelques clics,
            directement depuis l'application ou le site. La suppression est
            <strong> effective immédiatement</strong> : aucune demande à
            envoyer et aucun délai d'attente.
          </Typography>

          <Typography sx={{ mt: 2, fontWeight: 600 }}>
            Méthode A — Depuis votre compte (recommandé)
          </Typography>
          <Box component="ol" sx={{ pl: 3, my: 1 }}>
            <li>
              Connectez-vous sur{' '}
              <Link href="https://www.afroespace.com" sx={{ fontWeight: 500 }}>
                www.afroespace.com
              </Link>{' '}
              ou dans l'application AfroEspace.
            </li>
            <li>
              Ouvrez votre menu utilisateur, puis cliquez sur{' '}
              <strong>« Paramètres du compte »</strong>.
            </li>
            <li>
              Sélectionnez l'onglet{' '}
              <strong>« Supprimer mon compte »</strong> (en rouge, tout à
              droite).
            </li>
            <li>
              Saisissez votre mot de passe actuel (ou, pour les comptes
              connectés via Google, passez cette étape) puis tapez{' '}
              <strong>SUPPRIMER</strong> pour confirmer.
            </li>
            <li>
              Cliquez sur <strong>« Supprimer mon compte »</strong>. La
              suppression est effectuée immédiatement, toutes vos sessions
              sont fermées et vous êtes redirigé vers la page d'accueil.
            </li>
          </Box>

          <Typography sx={{ mt: 2, fontWeight: 600 }}>
            Méthode B — Par email (si vous n'avez plus accès au compte)
          </Typography>
          <Typography>
            Si vous ne pouvez plus vous connecter, envoyez un email à{' '}
            <Mail to="info@winorgroup.com" /> en précisant l'adresse email
            du compte à supprimer. Pour des raisons de sécurité, nous
            vérifions que vous êtes bien titulaire du compte avant de
            procéder. Délai de traitement : <strong>30 jours maximum</strong>.
          </Typography>
        </Section>

        <Section title="2. Données supprimées définitivement">
          <Typography>
            La suppression de votre compte entraîne l'effacement définitif des
            données suivantes :
          </Typography>
          <Box component="ul" sx={{ pl: 3, my: 1 }}>
            <li>Profil utilisateur (nom, prénom, email, numéro de téléphone)</li>
            <li>Photo de profil et photos de portfolio (pour les prestataires)</li>
            <li>Description, services, tarifs et horaires (pour les prestataires)</li>
            <li>Avis et notes laissés par vous ou reçus sur votre profil</li>
            <li>Messages et conversations envoyés via la messagerie intégrée</li>
            <li>Publications sur la section Opportunités</li>
            <li>Préférences de l'application (langue, pays, thème)</li>
          </Box>
        </Section>

        <Section title="3. Données conservées après suppression">
          <Typography>
            Certaines données sont conservées au-delà de la suppression du compte,
            uniquement pour des raisons légales ou de sécurité, conformément au
            RGPD :
          </Typography>
          <Box component="ul" sx={{ pl: 3, my: 1 }}>
            <li>
              <strong>Factures et données de transaction :</strong> 10 ans
              (obligation comptable française, article L.123-22 du Code de commerce).
            </li>
            <li>
              <strong>Logs de connexion anonymisés :</strong> 12 mois (sécurité et
              prévention de la fraude).
            </li>
            <li>
              <strong>Données nécessaires à la défense de droits :</strong> jusqu'à
              5 ans en cas de litige en cours.
            </li>
          </Box>
          <Typography>
            Ces données conservées sont strictement isolées et ne sont jamais
            utilisées à des fins commerciales ou de profilage.
          </Typography>
        </Section>

        <Section title="4. Suppression partielle de données">
          <Typography>
            Vous pouvez également demander la suppression d'éléments précis sans
            supprimer l'ensemble du compte : photo de profil, publication
            Opportunités, message, avis publié. Ces actions sont disponibles
            directement dans l'app ou peuvent être demandées par email à{' '}
            <Mail to="info@winorgroup.com" />.
          </Typography>
        </Section>

        <Section title="5. Contact">
          <Typography>
            Pour toute question relative à la suppression de votre compte ou à
            l'exercice de vos droits RGPD :
          </Typography>
          <Box component="ul" sx={{ pl: 3, my: 1, listStyle: 'none' }}>
            <li>📧 <Mail to="info@winorgroup.com" /></li>
            <li>🌐 <Link href="https://www.afroespace.com">www.afroespace.com</Link></li>
          </Box>
        </Section>
      </Box>
    </Container>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <>
      <Divider sx={{ my: 4 }} />
      <Typography variant="h5" component="h2" sx={{ mb: 2, fontWeight: 700 }}>
        {title}
      </Typography>
      <Stack spacing={1}>{children}</Stack>
    </>
  );
}

function Mail({ to }: { to: string }) {
  return (
    <Link href={`mailto:${to}`} sx={{ color: 'primary.main', fontWeight: 500 }}>
      {to}
    </Link>
  );
}
